import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { useExpenses } from './useExpenses';
import { useExpenseActions } from './useExpenseActions';

// O backend pode devolver um objeto em erros de validação; renderizar objeto
// como filho quebra o React, então coagimos para texto antes de exibir.
const toErrorText = (data) =>
  typeof data === 'string' && data ? data : 'Não foi possível concluir a operação.';

export const expenseStatusConfig = {
  'A Pagar': { color: 'warning', variant: 'outlined' },
  Pago: { color: 'success', variant: 'filled' },
  Atrasada: { color: 'error', variant: 'filled' },
};

// Orquestra a tela de saídas. Consumido pelas versões web e mobile, que ficam
// responsáveis apenas pela apresentação.
export function useExpensesScreen() {
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const {
    expenses,
    totalAmount,
    totalAmountToBePaid,
    totalAmountPaid,
    isLoading: isListLoading,
    error: listError,
    refetch: getExpenses,
  } = useExpenses(selectedDate);
  const { pay, remove, isLoading: isActionLoading } = useExpenseActions();

  const [openExpensesModal, setOpenExpensesModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSelectedExpense, setMenuSelectedExpense] = useState(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(toErrorText(listError));
    }
  }, [listError]);

  const goToPreviousMonth = useCallback(
    () =>
      setSelectedDate((previousDate) =>
        previousDate ? previousDate.subtract(1, 'month') : dayjs(),
      ),
    [],
  );

  const goToNextMonth = useCallback(
    () =>
      setSelectedDate((previousDate) => (previousDate ? previousDate.add(1, 'month') : dayjs())),
    [],
  );

  const saveSuccess = useCallback(
    (returnFromApi) => {
      setShowSuccessMessage(true);
      setSuccessMessage(toErrorText(returnFromApi.data));
      setOpenExpensesModal(false);
      getExpenses();
    },
    [getExpenses],
  );

  const openMenu = useCallback((event, expense) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedExpense(expense);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuSelectedExpense(null);
  }, []);

  const addExpense = useCallback(() => {
    closeMenu();
    setSelectedExpenseId(null);
    setOpenExpensesModal(true);
  }, [closeMenu]);

  const editExpense = useCallback(() => {
    setSelectedExpenseId(menuSelectedExpense.id);
    closeMenu();
    setOpenExpensesModal(true);
  }, [menuSelectedExpense, closeMenu]);

  const payExpense = useCallback(() => {
    if (!menuSelectedExpense) return;
    const expenseId = menuSelectedExpense.id;
    closeMenu();
    pay(expenseId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(toErrorText(returnFromApi.data));
        getExpenses();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toErrorText(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  }, [menuSelectedExpense, closeMenu, pay, getExpenses]);

  // O menu fecha, mas a saída selecionada precisa sobreviver até a confirmação
  const clickDelete = useCallback(() => {
    setMenuAnchorEl(null);
    setOpenDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setOpenDeleteModal(false);
    setMenuSelectedExpense(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!menuSelectedExpense) return;
    const expenseId = menuSelectedExpense.id;

    setOpenDeleteModal(false);
    setMenuSelectedExpense(null);
    remove(expenseId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(toErrorText(returnFromApi.data));
        getExpenses();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toErrorText(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  }, [menuSelectedExpense, remove, getExpenses]);

  return {
    expenses,
    totalAmount,
    totalAmountToBePaid,
    totalAmountPaid,
    isLoading: isListLoading || isActionLoading,

    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,

    menuAnchorEl,
    menuSelectedExpense,
    openMenu,
    closeMenu,

    openExpensesModal,
    setOpenExpensesModal,
    selectedExpenseId,
    addExpense,
    editExpense,
    payExpense,

    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,

    saveSuccess,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  };
}
